import React, { useState, useEffect } from 'react';
import { UserPlus, Settings, LogOut } from 'lucide-react';
import { getProfiles, setActiveProfile, clearActiveProfile } from '../../utils/profileManager';
import { playSound } from '../../utils/soundManager';
import ProfileCreator from './ProfileCreator';
import ProfileManager from './ProfileManager';

const ProfileSelector = ({ onProfileSelect }) => {
  const [profiles, setProfiles] = useState([]);
  const [showCreator, setShowCreator] = useState(false);
  const [showManager, setShowManager] = useState(false);

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = () => {
    const allProfiles = getProfiles();
    setProfiles(allProfiles);
  };

  const handleSelectProfile = (profile) => {
    playSound('click');
    setActiveProfile(profile.id);
    onProfileSelect(profile);
  };

  const handleCreateProfile = (newProfile) => {
    playSound('success');
    loadProfiles();
    setShowCreator(false);
    handleSelectProfile(newProfile);
  };

  const handleShowCreator = () => {
    playSound('click');
    setShowCreator(true);
  };

  const handleShowManager = () => {
    playSound('click');
    setShowManager(true);
  };

  const handleCloseCreator = () => {
    setShowCreator(false);
  };

  const handleCloseManager = () => {
    setShowManager(false);
    loadProfiles();
  };

  if (showCreator) {
    return (
      <ProfileCreator
        onProfileCreated={handleCreateProfile}
        onCancel={handleCloseCreator}
      />
    );
  }

  if (showManager) {
    return (
      <ProfileManager
        onClose={handleCloseManager}
        onProfilesUpdated={loadProfiles}
      />
    );
  }

  return (
    <div className="profile-selector-container">
      <div className="profile-selector-content animate-fadeIn">
        <div className="profile-selector-header">
          <h1 className="animate-slideInLeft">👋 Who's Learning Today?</h1>
          <p className="animate-slideInRight">Select your profile to continue</p>
        </div>

        <div className="profiles-grid">
          {profiles.map((profile, index) => (
            <div
              key={profile.id}
              className="profile-card hover-lift animate-scaleIn"
              onClick={() => handleSelectProfile(profile)}
              style={{
                animationDelay: `${index * 0.1}s`,
                borderTop: `4px solid ${profile.color}`
              }}
            >
              <div className="profile-avatar" style={{ background: profile.color }}>
                <span className="profile-avatar-emoji">{profile.avatar}</span>
              </div>
              <div className="profile-info">
                <h3 className="profile-name">{profile.name}</h3>
                <p className="profile-age">Age {profile.age}</p>
                <div className="profile-stats">
                  <span className="profile-stat">
                    🏆 {profile.earnedBadges?.length || 0} badges
                  </span>
                  <span className="profile-stat">
                    📝 {profile.stats?.totalQuizzes || 0} quizzes
                  </span>
                </div>
              </div>
            </div>
          ))}

          {/* Add New Profile Card */}
          <div
            className="profile-card profile-card-add hover-lift animate-scaleIn"
            onClick={handleShowCreator}
            style={{ animationDelay: `${profiles.length * 0.1}s` }}
          >
            <div className="profile-avatar profile-avatar-add">
              <UserPlus size={48} />
            </div>
            <div className="profile-info">
              <h3 className="profile-name">Add Profile</h3>
              <p className="profile-description">Create a new learner</p>
            </div>
          </div>
        </div>

        {profiles.length > 0 && (
          <div className="profile-selector-actions">
            <button
              className="profile-action-button hover-scale btn-press"
              onClick={handleShowManager}
            >
              <Settings size={20} />
              Manage Profiles
            </button>
          </div>
        )}

        <div className="profile-selector-footer">
          <p>🎓 Kids Learning Hub - Making Learning Fun!</p>
        </div>
      </div>
    </div>
  );
};

export default ProfileSelector;
